import { useEffect, useMemo, useState } from "react";
import { api, type BuildOptions, type CharacterDraft, type FinalizationResult, type GuidedBuildDefinition, type GuidedBuildPreview } from "./api.js";
import { emptyGenerationState, ensureGeneratedCharacter, generationContext, markGeneratedFieldManual, rerollGeneratedField, type GeneratedCharacterPayload, type GeneratedFieldKey } from "./character-generator.js";
import { copyToClipboard } from "./clipboard.js";
import { GeneratedIdentityFields } from "./GeneratedIdentityFields.js";
import { GuidedBuildStep } from "./GuidedBuildStep.js";
import { PortraitPicker } from "./PortraitPicker.js";

type StepId = "identity" | "build" | "story" | "review";

interface DraftPayload extends GeneratedCharacterPayload {
  playerName: string;
}

const EMPTY: DraftPayload = {
  characterName: "", nameKanji: "", nameMeaning: "", playerName: "", clan: "", clanDescription: "", characterConcept: "",
  background: "", appearance: "", notes: "", rumor: "", goal: "", bond: "", originId: "", arcanePowerIds: [], guidedAnswers: {}, generation: emptyGenerationState(),
};

function asText(value: unknown): string { return typeof value === "string" ? value : ""; }
function asStringArray(value: unknown): string[] { return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []; }
function asAnswers(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
}
function normalize(value: object): DraftPayload {
  const source = value as Record<string, unknown>;
  return {
    ...EMPTY, ...source,
    characterName: asText(source.characterName), nameKanji: asText(source.nameKanji), nameMeaning: asText(source.nameMeaning), playerName: asText(source.playerName),
    clan: asText(source.clan), clanDescription: asText(source.clanDescription), characterConcept: asText(source.characterConcept), background: asText(source.background),
    appearance: asText(source.appearance), notes: asText(source.notes), rumor: asText(source.rumor), goal: asText(source.goal), bond: asText(source.bond),
    originId: asText(source.originId), arcanePowerIds: asStringArray(source.arcanePowerIds), guidedAnswers: asAnswers(source.guidedAnswers),
    generation: source.generation && typeof source.generation === "object" ? source.generation as DraftPayload["generation"] : emptyGenerationState(),
  };
}
function issueText(issue: Record<string, unknown>): string {
  return typeof issue.message === "string" ? issue.message : typeof issue.code === "string" ? issue.code.replaceAll("_", " ") : "Rules issue";
}

export function BuilderMechanical({ draftId }: { draftId: string }) {
  const [draft, setDraft] = useState<CharacterDraft | null>(null);
  const [options, setOptions] = useState<BuildOptions | null>(null);
  const [definition, setDefinition] = useState<GuidedBuildDefinition | null>(null);
  const [preview, setPreview] = useState<GuidedBuildPreview | null>(null);
  const [payload, setPayload] = useState<DraftPayload>(EMPTY);
  const [step, setStep] = useState<StepId>("identity");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [finalized, setFinalized] = useState<FinalizationResult | null>(null);
  const [discordCode, setDiscordCode] = useState<string | null>(null);
  const [portraitVersion, setPortraitVersion] = useState(0);

  useEffect(() => { void load(); }, [draftId]);
  async function load() {
    try {
      const [nextDraft, nextOptions, nextDefinition] = await Promise.all([api.draft(draftId), api.buildOptions(draftId), api.guidedBuild(draftId)]);
      let nextPayload = normalize(nextDraft.draft_payload);
      setDraft(nextDraft); setOptions(nextOptions); setDefinition(nextDefinition);
      if (Object.keys(nextPayload.guidedAnswers).length === nextDefinition.requiredAnswerCount) {
        const generated = await api.previewGuidedBuild(draftId, nextPayload.guidedAnswers, nextPayload.originId || undefined, nextPayload.arcanePowerIds);
        setPreview(generated);
        nextPayload = ensureGeneratedCharacter({ ...nextPayload, ...generated.payloadPatch, originId: generated.originId, arcanePowerIds: generated.selectedPowerIds }, generationContext(nextOptions, nextDefinition, generated, nextPayload));
      }
      setPayload(nextPayload);
    } catch (error) { setMessage(error instanceof Error ? error.message : "The character could not be loaded."); }
  }

  const buildComplete = useMemo(() => definition
    ? Object.keys(payload.guidedAnswers).length === definition.requiredAnswerCount && payload.arcanePowerIds.length === 3 && Boolean(preview)
    : false, [definition, payload, preview]);

  function clearCompletion() {
    setFinalized(null);
    setDiscordCode(null);
  }
  function updateField(field: string, value: string) {
    setPayload((current) => ({ ...current, [field]: value })); clearCompletion();
  }
  function editGenerated(field: GeneratedFieldKey) { setPayload((current) => markGeneratedFieldManual(current, field)); }
  function reroll(field: GeneratedFieldKey) {
    if (!options || !definition || !preview) return;
    setPayload((current) => rerollGeneratedField(current, generationContext(options, definition, preview, current), field));
    clearCompletion();
  }
  function answer(questionId: string, answerId: string) {
    setPayload((current) => ({ ...current, guidedAnswers: { ...current.guidedAnswers, [questionId]: answerId } }));
    setPreview(null); clearCompletion();
  }
  async function generate(selectedPowerIds?: string[]) {
    if (!definition || !options) return;
    setBusy(true); setMessage(null);
    try {
      const generated = await api.previewGuidedBuild(draftId, payload.guidedAnswers, payload.originId || undefined, selectedPowerIds);
      setPreview(generated);
      setPayload((current) => {
        const merged = { ...current, ...generated.payloadPatch, guidedAnswers: current.guidedAnswers, originId: generated.originId, arcanePowerIds: generated.selectedPowerIds } as DraftPayload;
        return ensureGeneratedCharacter(merged, generationContext(options, definition, generated, merged));
      });
      clearCompletion();
    } catch (error) { setMessage(error instanceof Error ? error.message : "The specialized build could not be generated."); }
    finally { setBusy(false); }
  }
  function togglePower(powerId: string) {
    const current = payload.arcanePowerIds;
    const next = current.includes(powerId) ? current.filter((id) => id !== powerId) : current.length < 3 ? [...current, powerId] : current;
    setPayload((value) => ({ ...value, arcanePowerIds: next })); clearCompletion();
    if (next.length === 3) void generate(next);
  }
  async function uploadPortrait(dataUrl: string) {
    if (!draft) return;
    setBusy(true); setMessage(null);
    try {
      const result = await api.uploadDraftPortrait(draft.id, draft.lock_version, dataUrl);
      setDraft((current) => current ? { ...current, portrait_asset_id: result.assetId, lock_version: result.lockVersion, validation_status: "pending" } : current);
      setPortraitVersion((version) => version + 1);
      clearCompletion(); setMessage("Portrait attached to the character draft.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The portrait could not be uploaded.");
      throw error;
    } finally { setBusy(false); }
  }
  async function removePortrait() {
    if (!draft) return;
    setBusy(true); setMessage(null);
    try {
      const result = await api.removeDraftPortrait(draft.id, draft.lock_version);
      setDraft((current) => current ? { ...current, portrait_asset_id: null, lock_version: result.lockVersion, validation_status: "pending" } : current);
      setPortraitVersion((version) => version + 1);
      clearCompletion(); setMessage("The default portrait placeholder will be used.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The portrait could not be removed.");
      throw error;
    } finally { setBusy(false); }
  }
  async function save(next?: StepId): Promise<boolean> {
    if (!draft || finalized) return Boolean(finalized);
    setBusy(true); setMessage(null);
    try {
      const result = await api.saveDraft(draft.id, draft.lock_version, payload, draft.portrait_asset_id);
      setDraft((current) => current ? { ...current, draft_payload: payload, lock_version: result.lockVersion, validation_status: "pending" } : current);
      if (next) setStep(next);
      setMessage("Draft saved.");
      return true;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The draft could not be saved.");
      return false;
    } finally { setBusy(false); }
  }
  async function ensureFinalized(): Promise<FinalizationResult> {
    if (finalized) return finalized;
    if (!draft) throw new Error("The character draft is not available.");

    const saved = await api.saveDraft(draft.id, draft.lock_version, payload, draft.portrait_asset_id);
    setDraft((current) => current ? { ...current, draft_payload: payload, lock_version: saved.lockVersion, validation_status: "pending" } : current);

    const result = await api.finalizeDraft(draft.id);
    setFinalized(result);
    setDiscordCode(null);
    setDraft((current) => current ? { ...current, status: "finalized", finalized_revision_id: result.revisionId, validation_status: result.validationStatus } : current);
    return result;
  }
  async function exportErrorMessage(error: unknown): Promise<string> {
    if (draft) {
      try {
        const validation = await api.validateDraft(draft.id);
        if (!validation.valid && validation.errors.length > 0) return issueText(validation.errors[0]!);
      } catch {
        // Keep the original export error if diagnostics are unavailable.
      }
    }
    return error instanceof Error ? error.message : "The character export could not be prepared.";
  }
  async function downloadPdf() {
    if (!buildComplete) return;
    setBusy(true); setMessage(null);
    try {
      const result = await ensureFinalized();
      const link = document.createElement("a");
      link.href = api.pdfUrl(result.snapshotId, "editable");
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setMessage("Official character sheet generated.");
    } catch (error) {
      setMessage(await exportErrorMessage(error));
    } finally { setBusy(false); }
  }
  async function createDiscordCode() {
    if (!buildComplete) return;
    setBusy(true); setMessage(null);
    try {
      const completed = await ensureFinalized();
      const result = await api.issueImportToken(completed.revisionId);
      setDiscordCode(result.token);
      setMessage("Discord import code generated.");
    } catch (error) {
      setMessage(await exportErrorMessage(error));
    } finally { setBusy(false); }
  }
  async function copyDiscordCode() {
    if (!discordCode) return;
    try {
      await copyToClipboard(discordCode);
      setMessage("Discord import code copied.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The Discord import code could not be copied.");
    }
  }
  async function abandon() {
    if (!draft || !window.confirm("Abandon this character draft? This cannot be undone.")) return;
    setBusy(true);
    try { await api.abandonDraft(draft.id); window.location.assign("/"); }
    catch (error) { setMessage(error instanceof Error ? error.message : "The draft could not be abandoned."); setBusy(false); }
  }

  if (!draft || !options || !definition) return <main className="center"><div className="seal">妖</div><p>{message || "Opening character draft..."}</p></main>;
  const speciesName = options.species.label;
  const context = preview ? generationContext(options, definition, preview, payload) : null;
  const steps: StepId[] = ["identity", "build", "story", "review"];

  return <main className="builder-shell authenticated-builder">
    <header className="builder-topbar"><a href="/" className="back-link">Back to characters</a><div className="builder-title"><strong>{payload.characterName || "Unnamed yōkai"}</strong><span>{speciesName}</span></div><button className="text-button" disabled={busy || Boolean(finalized)} onClick={() => void save()}>Save draft</button></header>
    <div className="builder-layout">
      <aside className="step-rail" aria-label="Character builder steps">{steps.map((id, index) => <button key={id} className={step === id ? "active" : ""} onClick={() => setStep(id)}><span>{String(index + 1).padStart(2, "0")}</span>{id === "build" ? "Build" : id[0]!.toUpperCase() + id.slice(1)}</button>)}</aside>
      <section className="wizard-panel">
        {message && <div className="notice" role="status">{message}</div>}
        {step === "identity" && <div className="wizard-step"><p className="eyebrow">STEP ONE</p><h1>Who is playing?</h1><p className="step-intro">The character’s Japanese name, Clan, appearance, and story will be generated from the answers you give next.</p><div className="form-grid"><label><span>Player name</span><input disabled={Boolean(finalized)} value={payload.playerName} onChange={(event) => updateField("playerName", event.target.value)} /></label></div><div className="step-actions"><span /><button className="primary" disabled={busy || Boolean(finalized)} onClick={() => setStep("build")}>Begin the questionnaire</button></div></div>}
        {step === "build" && <div className="wizard-step wide-step"><p className="eyebrow">STEP TWO</p><h1>Shape the incarnation.</h1><p className="step-intro">Answer the questions. The same answers generate both a legal Yōkai! build and a coherent identity.</p><GuidedBuildStep definition={definition} answers={payload.guidedAnswers} preview={preview} originId={payload.originId} selectedPowerIds={payload.arcanePowerIds} busy={busy || Boolean(finalized)} onAnswer={answer} onOrigin={(originId) => { setPayload((current) => ({ ...current, originId })); setPreview(null); clearCompletion(); }} onPower={togglePower} onGenerate={() => void generate()} /><div className="step-actions"><button className="secondary" onClick={() => setStep("identity")}>Back</button><button className="primary" disabled={busy || !buildComplete || Boolean(finalized)} onClick={() => setStep("story")}>Continue to identity</button></div></div>}
        {step === "story" && <div className="wizard-step wide-step"><p className="eyebrow">STEP THREE</p><h1>Meet your yōkai.</h1>{context ? <GeneratedIdentityFields payload={payload} context={context} disabled={busy || Boolean(finalized)} onChange={updateField} onReroll={reroll} onEdit={editGenerated} /> : <div className="notice">Complete and generate the mechanical build first.</div>}<PortraitPicker previewUrl={api.draftPortraitUrl(draft.id, portraitVersion)} hasCustomPortrait={Boolean(draft.portrait_asset_id)} disabled={busy || Boolean(finalized)} onSelect={uploadPortrait} onRemove={removePortrait} /><div className="step-actions"><button className="secondary" onClick={() => setStep("build")}>Back</button><button className="primary" disabled={busy || !buildComplete || !payload.characterName.trim() || !payload.background.trim() || !payload.appearance.trim() || Boolean(finalized)} onClick={() => void save("review")}>Review character</button></div></div>}
        {step === "review" && <div className="wizard-step"><p className="eyebrow">STEP FOUR</p><h1>Your incarnation.</h1><dl className="review-list">
          <div><dt>Character</dt><dd>{payload.characterName || "Not provided"}{payload.nameKanji ? ` (${payload.nameKanji})` : ""}</dd></div><div><dt>Name meaning</dt><dd>{payload.nameMeaning || "Not provided"}</dd></div><div><dt>Species</dt><dd>{speciesName}</dd></div><div><dt>Clan</dt><dd>{payload.clan || "Not provided"}</dd></div><div><dt>Concept</dt><dd>{payload.characterConcept || "Not generated"}</dd></div><div><dt>Build</dt><dd>{preview ? `${preview.profile.generalSkillLabel} · ${preview.profile.attributeLabel}` : "Not generated"}</dd></div><div><dt>Origin</dt><dd>{definition.origins.find((item) => item.id === payload.originId)?.label || "Not selected"}</dd></div><div><dt>Arcane Powers</dt><dd>{payload.arcanePowerIds.length}</dd></div><div><dt>Portrait</dt><dd>{draft.portrait_asset_id ? "Custom portrait" : "Default species artwork"}</dd></div><div><dt>Background</dt><dd>{payload.background || "Not provided"}</dd></div>
        </dl>
          <section className="guest-export-card"><h2>Take your character with you</h2><p>Your character is saved to your account. Download the official sheet or generate a Discord import code.</p><div className="export-actions"><button className="primary" disabled={busy || !buildComplete} onClick={() => void downloadPdf()}>Download character PDF</button><button className="secondary" disabled={busy || !buildComplete} onClick={() => void createDiscordCode()}>{discordCode ? "Generate a new Discord code" : "Generate Discord code"}</button></div>{discordCode && <div className="discord-code"><span>Discord import code</span><strong>{discordCode}</strong><button className="secondary" onClick={() => void copyDiscordCode()}>Copy code</button><small>Valid for 30 days. Paste it into the official Yōkai! Discord bot when importing a character.</small></div>}</section>
          <div className="step-actions split">{finalized ? <a className="secondary" href="/">Back to characters</a> : <button className="danger-link" disabled={busy} onClick={() => void abandon()}>Abandon draft</button>}<div><button className="secondary" disabled={busy || Boolean(finalized)} onClick={() => setStep("story")}>Back</button></div></div>
        </div>}
      </section>
      <aside className="draft-summary"><p className="eyebrow">SAVED CHARACTER</p><h2>{payload.characterName || "Unnamed yōkai"}</h2><dl><div><dt>Species</dt><dd>{speciesName}</dd></div><div><dt>Build</dt><dd>{buildComplete ? "complete" : "incomplete"}</dd></div><div><dt>Storage</dt><dd>your account</dd></div></dl><p>{finalized ? "Your character is saved and ready for PDF or Discord play." : "Your progress is saved online while you build your yōkai."}</p></aside>
    </div>
  </main>;
}
